import { memo, type RefObject, useCallback, useEffect, useRef } from 'react';

import { Ai } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Textarea, type TextareaProps } from '@air/primitive-textarea';

const MIN_DESCRIPTION_LENGTH = 10;

export type BoardTemplateModalGenerateInputProps = {
  description: string;
  onDescriptionChange: (value: string) => void;
  onGenerate: () => void;
  isGenerating: boolean;
  isCreating: boolean;
  hasGeneratedBoards: boolean;
  textareaRef?: RefObject<HTMLTextAreaElement>;
};

export const BoardTemplateModalGenerateInput = memo(
  ({
    description,
    onDescriptionChange,
    onGenerate,
    isGenerating,
    isCreating,
    hasGeneratedBoards,
    textareaRef,
  }: BoardTemplateModalGenerateInputProps) => {
    const prevHasGeneratedBoards = useRef(hasGeneratedBoards);

    useEffect(() => {
      // Focus when boards are cleared (was true, now false)
      if (prevHasGeneratedBoards.current && !hasGeneratedBoards && textareaRef?.current) {
        const textarea = textareaRef.current;
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
      prevHasGeneratedBoards.current = hasGeneratedBoards;
    }, [hasGeneratedBoards, textareaRef]);

    const handleChange = useCallback<Required<TextareaProps>['onChange']>(
      (e) => {
        onDescriptionChange(e.target.value);
      },
      [onDescriptionChange],
    );

    return (
      <div className="flex w-1/2 flex-col gap-4">
        <div className="w-full">
          <label htmlFor="board-description" className="mb-2 block font-medium text-14 text-grey-12">
            Describe the board structure you want to create
          </label>
          <Textarea
            ref={textareaRef}
            id="board-description"
            value={description}
            onChange={handleChange}
            placeholder="e.g., Marketing campaign boards for Q1 with sub-boards for social media, email, and content"
            disabled={isGenerating || isCreating || hasGeneratedBoards}
            rows={3}
            className="w-full"
          />
          {description.trim().length > 0 && description.trim().length < MIN_DESCRIPTION_LENGTH && (
            <p className="mt-1 text-12 text-red-9">Please provide at least {MIN_DESCRIPTION_LENGTH} characters</p>
          )}
        </div>

        {!hasGeneratedBoards && (
          <Button
            onClick={onGenerate}
            disabled={description.trim().length < MIN_DESCRIPTION_LENGTH || isGenerating}
            isLoading={isGenerating}
            appearance="filled"
            color="blue"
            prefix={<Ai className="size-4" />}
          >
            Generate Structure
          </Button>
        )}
      </div>
    );
  },
);

BoardTemplateModalGenerateInput.displayName = 'BoardTemplateModalGenerateInput';
